import { createRoot } from 'react-dom/client';
import { useEffect, useState, useRef } from 'react';
import ToasterWrapper from '@/react/components/ToasterWrapper';
const { toast } = ToasterWrapper
import axios from 'axios';

import { RowSelection, Pagination, Table, ColumnEdit } from '@/react/libraries/FkEditTable/';
import useTimeline from '@/react/hooks/useTimeline';
import TableForm from '@/react/components/TableForm';
import Modal from '@/react/libraries/Modal';

// constants
import { COL_TO_PAYLOAD_KEY_MAP, HEADER_NAMES, COLUMNS_THAT_FORCE_RELOAD, API_URL, BASE_URL } from '@/react/pages/qc/base/{utils}/constants';

// functions
import { initStaticPage, initTableHeaders, validateDataOnCellSave, validateAndAddKeysToCellSavePayload, generateDependentDropdown } from '@/react/pages/qc/base/{utils}/functions';
import { LabelButtonUndo } from '@/react/components/TableActionButtons';
import { LabelButtonCustomizeColumns, LabelButtonDownload, LabelButtonRedo, LabelButton } from '@/react/components/TableActionButtons';

const Page = ({ payload }) => {
	// local variables
	const { stage, type, filter_id, title, period_map, visibleColumns, specific_retention_values } = payload

	// react hookes, states & refs
	const { addToUndoStack, undo, redo, isUndoStackEmpty, isRedoStackEmpty, clearTimeline } = useTimeline()

	const [headers, setHeaders] = useState([])
	const [isPageEnabled, setIsPageEnabled] = useState(true)
	const [isControlsEnabled, setIsControlsEnabled] = useState(true)
	const [selectedTableRows, setSelectedTableRows] = useState([])
	const [tableUrl, setTableUrl] = useState(`${API_URL}datatable/qcs?stage=${stage}&filter_id=${filter_id}&type=${type}&form_data={}`)
	const [filters, setFilters] = useState({})

	const tableRef = useRef(null)
	const paginationRef = useRef(null)
	const taxonomyDropdownRef = useRef(null)
	const routingNumberInputRef = useRef(null)
	const customizeColumnButtonRef = useRef(null)
	const modalRef = useRef(null)

	const setUIInteractivity = (active, cell) => {
		cell.setActive(active);
		setIsControlsEnabled(active);
	}

	const onDropdownOpen = async ({ cell, column, row }) => {
		generateDependentDropdown({ tableRef, cell, column, row })
	}

	const onCellDataSaveHandler = ({
		cell, column, newData: _newData, previousData: _previousData, row, additionalParams = {}
	}) => {
		const { fromTimeline } = additionalParams

		const dataObj = validateDataOnCellSave({
			cell, column, newData: _newData, previousData: _previousData, fromTimeline, tableRef, specific_retention_values, row
		})

		if (!dataObj) return

		const { newData, previousData } = dataObj

		const payload = {}
		payload.status = 3
		const api = `${BASE_URL}admin/cr/reviews/${row.dataset.rowId}`

		if (!validateAndAddKeysToCellSavePayload({
			payload,
			column,
			newData,
			COL_TO_PAYLOAD_KEY_MAP
		})) return

		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			// // update the no column
			if (data.status) {
				COLUMNS_THAT_FORCE_RELOAD.includes(column.heading) &&
					paginationRef.current.fetchData()


				if (!fromTimeline && previousData) {
					// basically saving manually, so add to undo stack
					addToUndoStack({ rowIndex: row.dataset.index, columnIndex: column.index, previousData, newData, type: 'normal' }, true)
				}

				return data.message
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}

		}).catch(err => {
			cell.updateData(previousData, false)

			if (err.response) {
				throw err.response.data.message
			} else {
				throw 'Something went wrong'
			}
		}).finally(() => setUIInteractivity(true, cell))

		toast.promise(promise, {
			loading: `${fromTimeline ? 'Reverting' : 'Saving'}...`,
			success: x => x,
			error: x => x,
		})

	}

	const applyBulkEdit = (_, undoElement) => {
		const selectedRows = [...selectedTableRows]
		const selectedTaxonomyName = taxonomyDropdownRef.current.getNames();
		const selectedTaxonomyValue = taxonomyDropdownRef.current.getValues();

		let previousColName = ''
		const previousRowData = []

		const dropdownToColumnNameMap = {
			'category': 'Category',
			'subcategory': 'Subcategory',
			'record_type': 'Record Type',
		}

		const updateTableForTaxonomy = (dropdownKey, payload) => {
			const colName = dropdownToColumnNameMap[dropdownKey];
			const colIndex = tableRef.current.columns.findIndex((col) => col.heading === colName);

			selectedRows.forEach((row) => {
				payload[COL_TO_PAYLOAD_KEY_MAP[colName]] = selectedTaxonomyValue[dropdownKey][0];

				payload.fk_qc_ids.push(row.rowId)

				previousColName = colName
				previousRowData.push({
					rowIndex: row.dataset.index,
					rowId: row.rowId,
					value: tableRef.current.getCellData(row.dataset.index, colIndex)
				})
			});
		};

		const payload = {}

		payload.status = 3
		payload.fk_qc_ids = []

		if (!undoElement) {
			if (Object.keys(selectedTaxonomyName).length === 0 && !routingNumberInputRef.current.value) return toast.error(
				'Please select a taxonomy/routing number to apply the changes');

			if (selectedRows.length === 0) return toast.error('Please select some rows to apply the changes');


			Object.keys(selectedTaxonomyName).forEach((dropdownKey) => {
				updateTableForTaxonomy(dropdownKey, payload);
			})

			if (routingNumberInputRef.current.value) {
				if (!window.checkRoutingNumberValidity(routingNumberInputRef.current.value))
					return toast.error('Please enter a valid routing number');
				payload.routing_number = routingNumberInputRef.current.value
			}

			const api = `${BASE_URL}admin/cr/bulk-update`;
			setIsPageEnabled(false)
			const promise = axios.post(api, payload, {
				headers: window.HEADER_OBJ
			})
				.then(({ data }) => {
					if (data.status) {
						// setUndoStack([...undoStack, { previousColName, previousRowData: [...previousRowData], type: 'bulk' }])
						addToUndoStack({ type: 'bulk' }, true)

						// refresh the table
						paginationRef.current.fetchData()

						return data.message
					} else {
						throw {
							response: {
								data: {
									message: data.message
								}
							}
						}
					}
				}).catch(err => {
					if (err.response) {
						throw err.response.data.message
					} else {
						throw 'Something went wrong'
					}
				}).finally(() => setIsPageEnabled(true))

			toast.promise(promise, {
				loading: 'Saving...',
				success: x => x,
				error: x => x,
			})
		}
	}

	const sendForReview = () => {
		const payload = {}
		payload.status = 4

		payload.fk_qc_ids = [...selectedTableRows].map(row => row.rowId)

		setIsPageEnabled(false)
		const api = `${BASE_URL}admin/cr/send-to-stage`
		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			if (data.status) {
				return data.message;
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}
		}).catch(err => {
			if (err.response) {
				throw err.response.data.message
			} else {
				throw "Couldn't send data for review. Please reload your browser"
			}
		}).finally(() => {
			setIsPageEnabled(true)
			paginationRef.current.fetchData()
		})

		toast.promise(promise, {
			loading: 'Sending for review...',
			success: x => x,
			error: x => x,
		})
	}

	const beforeSendForReview = () => {
		const selectedRows = [...selectedTableRows]
		if (selectedRows.length === 0) return toast.error('Please select some rows to send for review')

		// fire a swal!
		Swal.fire({
			title: `Send ${selectedRows.length} row(s) for review?`,
			text: "This will send the selected rows for review to the review team.",
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Yes, send it!'
		}).then((result) => {
			if (result.isConfirmed) {
				sendForReview()
			}
		})
	}


	const handleHistoryOperation = (operation, dataKey) => {
		const lastElement = operation();
		if (lastElement?.[dataKey] && lastElement.type === 'normal') {
			const { rowIndex, columnIndex, [dataKey]: data } = lastElement;
			tableRef.current.updateCell(rowIndex, columnIndex, data, true, { fromTimeline: true });
		} else if (lastElement && lastElement.type === 'bulk') {
			toast.error('Bulk edit cannot be undone/redone, your next undo/redo will be a normal cell edit, if any.')
		}
	};

	const handleUndo = () => handleHistoryOperation(undo, 'previousData');
	const handleRedo = () => handleHistoryOperation(redo, 'newData');


	useEffect(() => {
		initStaticPage({
			taxonomyDropdownRef,
			initROCategoryMultiLayer,
		})
		initTableHeaders({
			API_URL,
			setHeaders,
			HEADER_NAMES,
			visibleColumns,
			period_map,
		})

		fetch(`${BASE_URL}get-filter/${filter_id}`, {
			headers: window.HEADER_OBJ
		}).then(res => res.json()).then(data => {
			setFilters(data.filter_list)
		})
	}, [])

	return (
		<>
			<div className="page-card">
				<div className="">
					<h4 className="page-title">Continuous Review Taxonomy Assignment</h4>
					<p className='text-grey-text'>{title}</p>
				</div>
				<div disabled={!isPageEnabled || !isControlsEnabled}>
					<TableForm onSubmit={(e, obj) => {
						const form_data = encodeURIComponent(JSON.stringify(obj));
						const url = `${API_URL}datatable/qcs?stage=${stage}&filter_id=${filter_id}&type=${type}&form_data=${form_data}`;
						setTableUrl(url)
					}} />
				</div>
				<div>
					<div className="table-controls" disabled={!isControlsEnabled}>
						<div className="mr-auto flex items-center gap-2">
							<div id="taxonomyDropdown" className="w-full" data-multi-select="false"></div>
							<input className="input-field w-full" placeholder='Routing Number' ref={routingNumberInputRef} />
							<button className="btn btn-wide" onClick={applyBulkEdit}>
								Apply
							</button>
						</div>
						<LabelButtonUndo onClick={handleUndo} disabled={isUndoStackEmpty} />
						<LabelButtonRedo onClick={handleRedo} disabled={isRedoStackEmpty} />
						<LabelButtonCustomizeColumns ref={customizeColumnButtonRef} />
						<LabelButtonDownload href={`${BASE_URL}admin/qc/download?filter_id=${filter_id}&stage=${stage}`} />
						<LabelButton iconClass="material-symbols-rounded-new" iconText='info_i' onClick={modalRef.current?.openModal} content="See the filters applied to this table"
						/>
						<button className="btn btn-wide" onClick={beforeSendForReview}>Send for review</button>

						{/* <button className="btn" data-modal-trigger="#previewModal" onClick={(e) => {
							if (selectedTableRows.length === 0) {
								e.stopPropagation()
								toast.error('Please select some rows to preview')
							}
						}}>Preview</button> */}
					</div>
					<div>
						<Table headers={headers} onCellDataSave={onCellDataSaveHandler} onDropdownOpen={onDropdownOpen} ref={tableRef} className={
							`
							!min-w-0 !overflow-auto
							[&_[data-header="Select_Row"]]:sticky [&_[data-header="Select_Row"]]:left-0 [&_[data-header="Select_Row"]]:bg-white [&_[data-header="Select_Row"]]:shadow-[inset_-1px_0px_0px_0px_#00000014]

							[&_[data-header="Select_All"]]:sticky [&_[data-header="Select_All"]]:left-0 [data-header="Select_All"]]:top-0 [&_[data-header="Select_All"]]:bg-white [&_[data-header="Select_All"]]:z-20 [&_[data-header="Select_All"]]:shadow-[inset_-1px_0px_0px_0px_#00000014]

							[&_thead_th[data-header="Action"]]:!text-center
							[&_td[data-header="Legal_Reference_link"]]:break-all [&_td[data-header="What_to_Store"]]:min-w-[250px]`
						} />
						<RowSelection storageObject={setSelectedTableRows} />
						<Pagination className={'pt-6 pr-11'} url={tableUrl} offsetQueryParameter={"start"} lengthQueryParameter={"length"} ref={paginationRef} onPaginate={() => {
							clearTimeline()
						}} />
						<ColumnEdit buttonRef={customizeColumnButtonRef} metaKey="qc_columns" />

					</div>
				</div>
			</div>

			{/* filter modal */}
			<Modal ref={modalRef} className={`modal__content-wrapper p-2 `}>
				<div className="modal__content-wrapper-header">
					<h2>Applied filters</h2>
				</div>
				<div class="modal__content-wrapper-body">
					<div
						class="grid grid-cols-[1fr,0.75fr] grid-rows-[repeat(4,auto)] grid-flow-col gap-y-5 gap-x-20 max-h-[60%] max-w-[800px] overflow-y-auto [&>div]:flex [&>div]:flex-col [&>div]:gap-5">
						<div id="country">
							<FilterItem title='Country' filters={filters?.country} separator=", " />
						</div>
						<div id="state">
							<FilterItem title='State' filters={filters?.state} separator=', ' />
						</div>
						<div id="category">
							<FilterItem title='Category' filters={filters?.category} />
						</div>
						<div id="sub_category">
							<FilterItem title='Subcategory' filters={filters?.sub_category} />
						</div>
						<div id="record_type">
							<FilterItem title='Record Type' filters={filters?.record_type} />
						</div>
						<div id="keyword">
							<FilterItem title='Keyword' filters={filters?.keyword} />
						</div>
						<div id="number">
							<FilterItem title='Number' filters={filters?.number} />
						</div>
					</div>
				</div>
				<div className="modal__content-wrapper-footer">
					<button className='btn btn-outline-primary' onClick={modalRef.current?.closeModal}>Go back</button>
				</div>
			</Modal>
		</>
	);
}

const FilterItem = (
	{
		title,
		filters = ["__ __"],
		separator = "\n"
	}
) => {
	// if (!filters) return null
	return (
		<div>
			<h2 className='font-medium text-grey-text'>
				{title}
			</h2>
			{/* if separator is ',' show with comma */}
			{/* if separator is \n, show with line break */}
			{/* filters is an array */}
			<div className='text-sm'>
				{filters.join(separator)}
			</div>
		</div>
	)
}


function initPage(selector, payload) {
	createRoot(document.querySelector(selector)).render(
		<Page payload={payload} />
	);
}

export default initPage;
